import HomeWorkoutCard from './HomeWorkoutCard';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Moon, Scale, Smile, Meh, Frown, Dumbbell, Utensils, Play,
  Sparkles, Lock, Crown, ChevronRight, Calendar, Flame, Clock, Camera,
  ListChecks, X, Check, AlertTriangle, Bell, PartyPopper, CheckCircle2,
  Zap, Target,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMemberStatus, useUserProfile, useStreak, getDailyQuote, useCustomPlan, useTodayWorkout, useMemberTrainingPlan, useTodayDiet, useDietPlans } from '@/hooks/useAppState';
import { avatarImages, logger } from '@lark-apaas/client-toolkit-lite';
import { Image } from '@/components/ui/image';
import userStorage from '@/lib/user-storage';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import HealthCheckInSheet from '@/components/HealthCheckInSheet';
import HealthCalendarSheet from '@/components/HealthCalendarSheet';
import AerobicDetailSheet from '@/components/AerobicDetailSheet';
import { DEFAULT_AVATAR } from '@/data/sport-avatars';
import ProfileEditDialog from '@/components/ProfileEditDialog';
import MemberLockDialog from '@/components/MemberLockDialog';
import { useDailyHealth } from '@/hooks/useDailyHealth';
import { useComparePhotos } from '@/hooks/useComparePhotos';
import { useDailyReminder } from '@/hooks/useDailyReminder';

export default function HomePage() {
  const navigate = useNavigate();
  const { member } = useMemberStatus();
  const { profile, updateProfile } = useUserProfile();
  const { streak, lastCheckin } = useStreak();
  const { plan } = useCustomPlan();
  const todayWorkout = useTodayWorkout();
  const { memberPlan, getTodayWeekDay, todayInjuryRecovery, todayRecoveryCompleted, todayAerobicCompleted, markTodayAerobicComplete, recoveryStatus, dailyRelaxPlan, recoveryInfo } = useMemberTrainingPlan();
  const todayDiet = useTodayDiet();
  const { hasPlan: hasDietPlan } = useDietPlans();
  const todayMemberDay = getTodayWeekDay();
  const quote = getDailyQuote();
  const { latestRecord, todayRecord, healthStreak } = useDailyHealth();
  const { isTodayPhotoDay, isTodayPhotoTaken, currentDay: currentPhotoDay } = useComparePhotos();

  const displayName = profile.name || '学员';
  const displayAvatar = profile.avatar || DEFAULT_AVATAR;

  const handleSaveProfile = (data: { name: string; avatar: string }) => {
    updateProfile({ name: data.name, avatar: data.avatar });
  };

  // 首页展示用的健康数据：优先取今日录入，其次最近一次录入，最后取 profile 基础值（仅非会员 mock）
  const displayHeartRate = todayRecord?.restingHeartRate ?? latestRecord?.restingHeartRate ?? profile.restingHeartRate;
  const displaySleepHours = todayRecord?.sleepHours ?? latestRecord?.sleepHours ?? profile.sleepHours;
  const displayWeight = todayRecord?.weight ?? latestRecord?.weight ?? profile.weight;
  const displayMood = todayRecord?.mood ?? latestRecord?.mood;

  const moodInfo = (() => {
    if (displayMood === 'great') return { label: '很好', labelShort: '很好', icon: Smile, color: 'text-success' };
    if (displayMood === 'poor') return { label: '不佳', labelShort: '不佳', icon: Frown, color: 'text-destructive' };
    if (displayMood === 'normal') return { label: '一般', labelShort: '一般', icon: Meh, color: 'text-warning' };
    return null;
  })();

  const [healthCheckInOpen, setHealthCheckInOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [aerobicOpen, setAerobicOpen] = useState(false);
  const [memberLockOpen, setMemberLockOpen] = useState(false);
  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [planTab, setPlanTab] = useState<'workout' | 'diet' | 'recovery'>('workout');

  const { settings: reminderSettings, enableReminders, startReminderLoop } = useDailyReminder();

  const [dateInfo, setDateInfo] = useState('');
  const [greeting, setGreeting] = useState('早上好');

  useEffect(() => {
    const now = new Date();
    const hour = now.getHours();
    let g = '早上好';
    if (hour >= 5 && hour < 11) g = '早上好';
    else if (hour >= 11 && hour < 13) g = '中午好';
    else if (hour >= 13 && hour < 18) g = '下午好';
    else if (hour >= 18 && hour < 23) g = '晚上好';
    else g = '夜深了';
    setGreeting(g);

    const month = now.getMonth() + 1;
    const date = now.getDate();
    const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    const wd = weekDays[now.getDay()];
    // 仅会员有专属计划时显示训练日名称；普通用户不派生训练日
    const dayLabel = memberPlan && todayMemberDay?.type === 'strength' ? todayMemberDay.label : '';
    setDateInfo(`${month}月${date}日 ${wd}${dayLabel ? `·${dayLabel}` : ''}`);
  }, [memberPlan, todayMemberDay]);

  // 饮食打卡完成状态
  const dietCompleted = useMemo(() => {
    const today = new Date().toDateString();
    try {
      const raw = userStorage.getItem(`diet_checkin_${today}`);
      if (raw) {
        const statuses = JSON.parse(raw) as Record<string, string>;
        const mealKeys = ['breakfast', 'lunch', 'dinner', 'snack'];
        const doneCount = mealKeys.filter(k => statuses[k] === 'completed' || statuses[k] === 'actual').length;
        return doneCount >= 3;
      }
    } catch { /* ignore */ }
    return false;
  }, []);

  // ========== 今日待办计算 ==========
  const healthCompleted = todayRecord !== null && (
    todayRecord.restingHeartRate !== undefined
    && todayRecord.sleepHours !== undefined
    && todayRecord.weight !== undefined
    && todayRecord.mood !== undefined
  );
  const healthCheckInCount = todayRecord
    ? [todayRecord.restingHeartRate, todayRecord.sleepHours, todayRecord.weight, todayRecord.mood].filter(v => v !== undefined).length
    : 0;

  const handleGoTraining = () => {
    navigate('/training?tab=plans');
  };

  const handleStartTraining = () => {
    if (todayWorkout.hasPlan) {
      navigate(`/day/${todayWorkout.dayId}`);
    }
  };

  const todoItems = useMemo(() => {
    const items: {
      key: string;
      label: string;
      subLabel: string;
      icon: typeof Dumbbell;
      color: string;
      completed: boolean;
      onClick: () => void;
    }[] = [];

    // 今日训练（统一使用 todayMemberDay.type 作为 dayType 单一事实来源）
    // - strength 力量训练日 → 「今天该练XX」/ 已完成显示「力量训练已完成」
    // - cardio / hiit 有氧日 → 「有氧训练 · XX · 约XX分钟」/ 已完成显示「有氧训练已完成」
    // - active-rest 主动恢复日 → 「今日主动恢复」
    // - rest 休息日 → 「今日休息」
    // 注意：shouldRestToday 是健康异常时的「建议休息」，不改变 dayType 本身的分类
    const dayType = todayMemberDay?.type || 'rest';
    const isRestDay = dayType === 'rest';
    const isStrengthDay = dayType === 'strength';
    const isCardioDay = dayType === 'cardio' || dayType === 'hiit';
    const isActiveRestDay = dayType === 'active-rest';
    const hasTrainingContent = !isRestDay;
    const shouldRest = todayWorkout.shouldRestToday;

    const strengthCompleted = todayWorkout.isCompletedToday || todayWorkout.status === 'completed';
    const cardioCompleted = todayAerobicCompleted && isCardioDay;
    const activeRestCompleted = todayAerobicCompleted && isActiveRestDay;
    const workoutCompleted = isStrengthDay ? strengthCompleted : (isCardioDay || isActiveRestDay) ? cardioCompleted || activeRestCompleted : false;

    const workoutLabel = todayMemberDay?.label || '今日训练';
    const durationMin = todayWorkout.durationMin || todayMemberDay?.durationMin || 0;
    const cardioItem = todayMemberDay?.cardioItem || todayMemberDay?.subtitle || '';

    const handleTrainingClick = () => {
      if (isStrengthDay && todayWorkout.hasPlan) {
        navigate(`/day/${todayWorkout.dayId}`);
      } else if (isCardioDay || isActiveRestDay) {
        setAerobicOpen(true);
      } else {
        navigate('/training?tab=plans');
      }
    };
    let todoLabel = '今日休息';
    let todoSubLabel = '好好恢复身体';
    let todoIcon = Dumbbell;
    let todoColor = 'bg-primary/15 text-primary';
    if (isStrengthDay) {
      if (workoutCompleted) {
        todoLabel = '力量训练已完成 ✓';
        todoSubLabel = '今天练得很棒，明天继续';
      } else if (shouldRest) {
        todoLabel = '今日建议休息';
        todoSubLabel = '身体状态欠佳，训练顺延';
      } else {
        todoLabel = `今天该练${workoutLabel}`;
        todoSubLabel = '训练后记得打卡';
      }
    } else if (isCardioDay) {
      if (workoutCompleted) {
        todoLabel = '有氧训练已完成 ✓';
        todoSubLabel = `已完成 ${durationMin || '约 30'} 分钟有氧`;
      } else if (shouldRest) {
        todoLabel = '今日建议休息';
        todoSubLabel = '身体状态欠佳，有氧改日';
      } else {
        todoLabel = '有氧训练';
        todoSubLabel = `${cardioItem || '有氧训练'} · 约 ${durationMin || 30} 分钟`;
      }
      todoIcon = Heart;
      todoColor = workoutCompleted ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning';
    } else if (isActiveRestDay) {
      if (workoutCompleted) {
        todoLabel = '主动恢复已完成 ✓';
        todoSubLabel = '身体状态正在恢复';
      } else if (shouldRest) {
        todoLabel = '今日建议休息';
        todoSubLabel = '身体状态欠佳，多休息';
      } else {
        todoLabel = '今日主动恢复';
        todoSubLabel = todayMemberDay?.subtitle || '轻量活动，促进恢复';
      }
      todoIcon = Sparkles;
      todoColor = workoutCompleted ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground';
    }
    // 训练待办：仅会员 + 有训练计划时显示
    const hasWorkoutPlan = member.isMember && !!memberPlan;
    if (hasWorkoutPlan) {
      items.push({
        key: 'workout',
        label: todoLabel,
        subLabel: todoSubLabel,
        icon: todoIcon,
        color: todoColor,
        completed: hasTrainingContent ? (shouldRest ? true : workoutCompleted) : true,
        onClick: handleTrainingClick,
      });
    }

    // 饮食打卡：有饮食方案才显示
    const today = new Date().toDateString();
    let dietCompleted = false;
    try {
      const raw = userStorage.getItem(`diet_checkin_${today}`);
      if (raw) {
        const statuses = JSON.parse(raw) as Record<string, string>;
        // 三餐 + 加餐至少 3 餐 completed/actual 视为已打卡
        const mealKeys = ['breakfast', 'lunch', 'dinner', 'snack'];
        const doneCount = mealKeys.filter(k => statuses[k] === 'completed' || statuses[k] === 'actual').length;
        dietCompleted = doneCount >= 3;
      }
    } catch { /* ignore */ }

    if (hasDietPlan) {
      items.push({
        key: 'diet',
        label: dietCompleted ? '饮食打卡已完成' : '饮食还没打卡',
        subLabel: dietCompleted ? '今日饮食记录良好' : '记录今日实际摄入',
        icon: Utensils,
        color: 'bg-emerald-500/15 text-emerald-600',
        completed: dietCompleted,
        onClick: () => navigate('/diet'),
      });
    }

    // 健康打卡
    items.push({
      key: 'health',
      label: healthCompleted ? '健康打卡已完成' : '健康打卡未完成',
      subLabel: healthCompleted ? '4 项全部录入' : `已录 ${healthCheckInCount}/4 项`,
      icon: Heart,
      color: 'bg-info/20 text-info',
      completed: healthCompleted,
      onClick: () => setHealthCheckInOpen(true),
    });

    return items;
  }, [todayWorkout, todayMemberDay, todayAerobicCompleted, todayRecord, navigate, healthCompleted, healthCheckInCount, member.isMember, memberPlan, hasDietPlan]);

  const allCompleted = todoItems.every(i => i.completed);
  const completedCount = todoItems.filter(i => i.completed).length;

  // 今日是否已完成训练或饮食打卡（用于时机化转化文案）
  const todayTrainedOrDieted = useMemo(() => {
    const workoutDone = todayWorkout.isCompletedToday || todayWorkout.status === 'completed';
    // 从 todoItems 中直接读取饮食完成状态，避免重复计算
    const dietItem = todoItems.find(i => i.key === 'diet');
    return workoutDone || !!dietItem?.completed;
  }, [todayWorkout.isCompletedToday, todayWorkout.status, todoItems]);

  // 开启提醒定时轮询（页面打开时持续检查）
  useEffect(() => {
    if (!reminderSettings.enabled) return;
    const cleanup = startReminderLoop(() => {
      const isTrainingDay = todayWorkout.hasPlan && todayWorkout.dayType !== 'rest' && !todayWorkout.shouldRestToday;
      const workoutCompleted = todayWorkout.isCompletedToday || todayWorkout.status === 'completed';
      const today = new Date().toDateString();
      let dietCompleted = false;
      let healthCompleted = false;
      try {
        const raw = userStorage.getItem(`diet_checkin_${today}`);
        if (raw) {
          const statuses = JSON.parse(raw) as Record<string, string>;
          const mealKeys = ['breakfast', 'lunch', 'dinner', 'snack'];
          const doneCount = mealKeys.filter(k => statuses[k] === 'completed' || statuses[k] === 'actual').length;
          dietCompleted = doneCount >= 3;
        }
      } catch { /* ignore */ }
      if (todayRecord) {
        healthCompleted = todayRecord.restingHeartRate !== undefined
          && todayRecord.sleepHours !== undefined
          && todayRecord.weight !== undefined
          && todayRecord.mood !== undefined;
      }
      return {
        isTrainingDay,
        workoutLabel: todayMemberDay?.label || '',
        hasCompletedWorkout: workoutCompleted,
        hasCompletedDiet: dietCompleted,
        hasCompletedHealth: healthCompleted,
      };
    });
    return cleanup;
  }, [reminderSettings.enabled, startReminderLoop, todayWorkout, todayMemberDay, todayRecord]);

  const handleEnableReminders = async () => {
    const granted = await enableReminders();
    if (granted) {
      toast.success('已开启每日提醒');
    } else {
      toast.info('已开启站内待办，浏览器通知未授权');
    }
  };

  const handleCustomPlan = () => {
    if (member.isMember) {
      navigate('/custom');
    } else {
      navigate('/subscribe');
    }
  };

  return (
    <div className="min-h-screen px-4 pt-6 pb-6">
      {/* 顶部信息栏 */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex-1">
          <h1 className="text-[26px] font-bold text-foreground leading-tight">
            {greeting}，{displayName}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{dateInfo}</p>
          <p className="text-[13px] text-muted-foreground/80 mt-1.5 truncate">
            "{quote}"
          </p>
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setProfileEditOpen(true)}
            className="block active:opacity-80 transition-opacity"
            aria-label="编辑资料"
          >
            <Image
              src={displayAvatar}
              alt="头像"
              className="w-12 h-12 rounded-full border-2 border-white shadow-md object-cover"
            />
          </button>
        </div>
      </div>

      {/* 今日待办区域 */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-1.5">
            <ListChecks className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">今日待办</span>
            <span className="text-[10px] text-muted-foreground ml-1">
              {completedCount}/{todoItems.length}
            </span>
          </div>
          {!reminderSettings.enabled ? (
            <button
              onClick={handleEnableReminders}
              className="flex items-center gap-1 text-[11px] text-primary font-medium active:opacity-80 transition-opacity"
            >
              <Bell className="w-3 h-3" />
              开启提醒
            </button>
          ) : reminderSettings.notificationGranted ? (
            <span className="flex items-center gap-1 text-[11px] text-success font-medium">
              <CheckCircle2 className="w-3 h-3" />
              提醒已开
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[11px] text-warning font-medium">
              <Bell className="w-3 h-3" />
              仅站内
            </span>
          )}
        </div>

        {allCompleted ? (
          <div className="metallic-card rounded-2xl p-5 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-success/20 to-success/5 flex items-center justify-center mb-2">
              <PartyPopper className="w-6 h-6 text-success" />
            </div>
            <p className="text-base font-bold text-foreground">今日全部完成 🎉</p>
            <p className="text-xs text-muted-foreground mt-1">
              今天的训练、饮食、健康打卡都搞定了，继续保持！
            </p>
          </div>
        ) : (
          <div className="metallic-card rounded-2xl p-3 space-y-2">
            {todoItems.map(item => (
              <button
                key={item.key}
                onClick={item.onClick}
                className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all active:scale-[0.98] text-left ${
                  item.completed ? 'opacity-60' : ''
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                  <item.icon className="w-4.5 h-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold leading-tight ${
                    item.completed ? 'text-muted-foreground line-through' : 'text-foreground'
                  }`}>
                    {item.label}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                    {item.subLabel}
                  </p>
                </div>
                <div className="shrink-0">
                  {item.completed ? (
                    <div className="w-6 h-6 rounded-full bg-success/15 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-success" />
                    </div>
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 身体数据胶囊（紧凑一行） */}
      <div className="mb-3">
        <div className="flex gap-1.5">
          <div className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-full bg-card border border-border/40 text-foreground text-[10px] font-medium min-w-0">
            <Heart className="w-3 h-3 shrink-0 text-primary/70" />
            {todayRecord?.restingHeartRate !== undefined ? (
              <span className="truncate tabular-nums">{displayHeartRate}</span>
            ) : (
              <span className="truncate text-muted-foreground">--</span>
            )}
          </div>

          <div className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-full bg-card border border-border/40 text-foreground text-[10px] font-medium min-w-0">
            <Moon className="w-3 h-3 shrink-0 text-info/70" />
            {todayRecord?.sleepHours !== undefined ? (
              <span className="truncate tabular-nums">{displaySleepHours}h</span>
            ) : (
              <span className="truncate text-muted-foreground">--</span>
            )}
          </div>

          <div className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-full bg-card border border-border/40 text-foreground text-[10px] font-medium min-w-0">
            <Scale className="w-3 h-3 shrink-0 text-muted-foreground" />
            {todayRecord?.weight !== undefined ? (
              <span className="truncate tabular-nums">{displayWeight}</span>
            ) : (
              <span className="truncate text-muted-foreground">--</span>
            )}
          </div>

          <div className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-full bg-card border border-border/40 text-foreground text-[10px] font-medium min-w-0">
            {moodInfo ? (
              <>
                <moodInfo.icon className={`w-3 h-3 shrink-0 ${moodInfo.color} opacity-80`} />
                <span className="truncate">{moodInfo.labelShort}</span>
              </>
            ) : (
              <>
                <Smile className="w-3 h-3 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground truncate">--</span>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => setCalendarOpen(true)}
            className="shrink-0 w-8 h-8 rounded-full bg-card border border-border/50 flex items-center justify-center active:scale-95 transition-transform"
          >
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>
      {/* /身体数据胶囊 */}

      {/* 今日打卡按钮（紧挨健康数据下方） */}
      <button
        onClick={() => setHealthCheckInOpen(true)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all active:scale-[0.98] mb-3 ${
          healthCompleted
            ? 'bg-gradient-to-r from-success/20 via-success/15 to-success/10 border border-success/30'
            : 'bg-gradient-to-r from-primary/20 via-primary/15 to-primary/10 border border-primary/30'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${
            healthCompleted
              ? 'bg-gradient-to-br from-success to-success/80'
              : 'bg-gradient-to-br from-primary to-primary/80'
          }`}>
            {healthCompleted ? (
              <Check className="w-5 h-5 text-white" />
            ) : (
              <Target className="w-5 h-5 text-white" />
            )}
          </div>
          <div className="text-left">
            <p className="text-sm font-bold text-foreground">
              {healthCompleted ? '今日打卡已完成' : '健康打卡'}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {healthCompleted
                ? '4 项全部记录，继续保持'
                : `已录 ${healthCheckInCount}/4 项，点击录入`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${
            healthCompleted
              ? 'bg-success/20 text-success'
              : 'bg-white/60 text-primary backdrop-blur-sm'
          }`}>
            {healthCompleted ? '✓ 全部完成' : '去打卡'}
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </button>

      {/* 今日计划 · 三合一 Tab 卡 */}
      <div className="metallic-card rounded-2xl overflow-hidden mb-3">
        {/* Tab 切换栏 */}
        <div className="flex items-center p-1 gap-1 border-b border-border/40">
          {[
            { key: 'workout' as const, label: '训练', icon: Dumbbell, dot: false },
            { key: 'diet' as const, label: '饮食', icon: Utensils, dot: !dietCompleted },
            { key: 'recovery' as const, label: '恢复', icon: Sparkles, dot: false },
          ].map(tab => (
             <button
               key={tab.key}
               onClick={() => {
                 if (tab.key === 'recovery' && !member.isMember) {
                   setMemberLockOpen(true);
                   return;
                 }
                 setPlanTab(tab.key);
               }}
               className={`relative min-w-0 flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all ${
                 planTab === tab.key
                   ? 'bg-primary text-primary-foreground shadow-sm'
                   : 'text-muted-foreground hover:text-foreground'
               }`}
             >
               <tab.icon className="w-3.5 h-3.5 shrink-0" />
               {tab.label}
               {tab.key === 'recovery' && !member.isMember && (
                 <span className="inline-flex shrink-0 items-center justify-center rounded-full border border-destructive bg-destructive px-1.5 text-[9px] font-semibold leading-[14px] text-destructive-foreground">
                   VIP
                 </span>
               )}
               {tab.dot && (
                 <span className="absolute top-1.5 right-3 w-1.5 h-1.5 rounded-full bg-destructive" />
               )}
             </button>
          ))}
        </div>

        {/* Tab 内容 */}
        <div className="p-4">
          {/* 训练 Tab */}
          {planTab === 'workout' && (
            <div>
              {/* 拍照日提醒（会员节点日内联） */}
              {member.isMember && isTodayPhotoDay && !isTodayPhotoTaken && (
                <button
                  onClick={() => navigate('/cycle-report')}
                  className="w-full flex items-center gap-2.5 p-3 rounded-xl bg-primary/10 border border-primary/20 mb-3 text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                    <Camera className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-foreground">
                      今日是拍照日 · 第 {currentPhotoDay} 天
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      记得拍对比照，记录变化
                    </p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                </button>
              )}

              <HomeWorkoutCard setAerobicOpen={setAerobicOpen} />
            </div>
          )}

          {/* 饮食 Tab */}
          {planTab === 'diet' && (
            !hasDietPlan ? (
              <div
                onClick={() => navigate(member.isMember ? '/detailed-assessment' : '/simple-assessment')}
                className="cursor-pointer rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 p-4 border border-emerald-500/20"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <Utensils className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-foreground">生成你的饮食方案</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">30秒问卷，定制碳循环饮食计划</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-emerald-600 shrink-0" />
                </div>
                <div className="flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-500/10 rounded-lg px-2.5 py-1.5">
                  <Sparkles className="w-3.5 h-3.5 shrink-0" />
                  <span>根据你的目标、训练频率、饮食偏好生成方案</span>
                </div>
              </div>
            ) : (
              <div onClick={() => navigate('/diet')} className="cursor-pointer">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      todayDiet.isTrainingDay ? 'bg-warning' : todayDiet.carbDay === 'medium' ? 'bg-info' : 'bg-primary'
                    }`}></div>
                    <h3 className="text-base font-bold text-foreground">
                      今日饮食·{todayDiet.carbDayLabel}
                    </h3>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
                {todayDiet.targets ? (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-sm font-bold text-foreground">
                        {todayDiet.targets.carb}
                        <span className="text-[10px] font-normal text-muted-foreground ml-0.5">g</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground">碳水</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">
                        {todayDiet.targets.protein}
                        <span className="text-[10px] font-normal text-muted-foreground ml-0.5">g</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground">蛋白</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">
                        {todayDiet.targets.fat}
                        <span className="text-[10px] font-normal text-muted-foreground ml-0.5">g</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground">脂肪</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-sm font-bold text-foreground">255<span className="text-[10px] font-normal text-muted-foreground ml-0.5">g</span></p>
                      <p className="text-[10px] text-muted-foreground">碳水</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">128<span className="text-[10px] font-normal text-muted-foreground ml-0.5">g</span></p>
                      <p className="text-[10px] text-muted-foreground">蛋白</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">43<span className="text-[10px] font-normal text-muted-foreground ml-0.5">g</span></p>
                      <p className="text-[10px] text-muted-foreground">脂肪</p>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          {/* 恢复 Tab */}
          {planTab === 'recovery' && member.isMember && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${recoveryStatus === 'good' ? 'bg-success' : 'bg-info'}`}></div>
                  <h3 className="text-base font-bold text-foreground">
                    {recoveryStatus === 'good'
                      ? '身体状态良好'
                      : todayInjuryRecovery?.title || '今日恢复'}
                  </h3>
                </div>
                {todayRecoveryCompleted && (
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/15">
                    <Check className="w-3 h-3 text-success" />
                    <span className="text-[10px] font-bold text-success">已完成</span>
                  </div>
                )}
              </div>

              {recoveryStatus === 'good' && (
                <div>
                  <div className={`flex items-center gap-2 p-3 rounded-xl mb-3 ${
                    recoveryInfo.sleepTipLevel === 'good' ? 'bg-success/10' :
                    recoveryInfo.sleepTipLevel === 'warn' ? 'bg-warning/10' :
                    recoveryInfo.sleepTipLevel === 'poor' ? 'bg-destructive/10' :
                    'bg-muted/50'
                  }`}>
                    <Moon className={`w-4 h-4 shrink-0 ${
                      recoveryInfo.sleepTipLevel === 'good' ? 'text-success' :
                      recoveryInfo.sleepTipLevel === 'warn' ? 'text-warning' :
                      recoveryInfo.sleepTipLevel === 'poor' ? 'text-destructive' :
                      'text-muted-foreground'
                    }`} />
                    <p className={`text-xs leading-relaxed font-medium ${
                      recoveryInfo.sleepTipLevel === 'good' ? 'text-success' :
                      recoveryInfo.sleepTipLevel === 'warn' ? 'text-warning' :
                      recoveryInfo.sleepTipLevel === 'poor' ? 'text-destructive' :
                      'text-muted-foreground'
                    }`}>
                      {recoveryInfo.sleepTip}
                    </p>
                  </div>
                  {recoveryInfo.moodRelaxTip && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-info/10 mb-3">
                      <Smile className="w-4 h-4 text-info shrink-0" />
                      <p className="text-xs text-info leading-relaxed font-medium">
                        {recoveryInfo.moodRelaxTip}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => navigate('/recovery-workout?mode=relax')}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-border/50 hover:bg-card/50 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-sm font-medium text-foreground">做日常放松</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {dailyRelaxPlan.totalMin} 分钟 · {dailyRelaxPlan.actions.length} 个轻量动作
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                </div>
              )}
              {recoveryStatus !== 'good' && todayInjuryRecovery && (
                <Sheet>
                  <SheetTrigger asChild>
                    <button className="w-full text-left">
                      <p className="text-xs text-muted-foreground mb-2">
                        {todayInjuryRecovery.totalMin} 分钟 · 共 {todayInjuryRecovery.actions.length} 个动作
                      </p>
                      <div className="space-y-1.5">
                        {todayInjuryRecovery.actions.slice(0, 3).map(a => (
                          <div key={a.id} className="flex items-center justify-between text-xs">
                            <span className="text-foreground">{a.name}</span>
                            <span className="text-muted-foreground">{a.suggestion}</span>
                          </div>
                        ))}
                        {todayInjuryRecovery.actions.length > 3 && (
                          <p className="text-[10px] text-primary">展开查看全部 →</p>
                        )}
                      </div>
                    </button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="rounded-t-2xl h-[70vh]">
                    <SheetHeader>
                      <SheetTitle className="text-left">{todayInjuryRecovery.title}</SheetTitle>
                      <p className="text-xs text-muted-foreground text-left">
                        约 {todayInjuryRecovery.totalMin} 分钟 · {todayInjuryRecovery.actions.length} 个动作
                      </p>
                    </SheetHeader>
                    <div className="mt-4 space-y-3 overflow-y-auto max-h-[calc(70vh-140px)]">
                      {todayInjuryRecovery.actions.map((a, idx) => (
                        <div key={a.id} className="p-3 rounded-xl bg-card border border-border/40">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-primary font-medium">动作 {idx + 1}</span>
                            <span className="text-xs text-muted-foreground">{a.suggestion}</span>
                          </div>
                          <p className="text-sm font-semibold text-foreground">{a.name}</p>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            {a.category === 'foam-roller' ? '泡沫轴放松，缓慢滚动痛点附近区域' :
                             a.category === 'stretch' ? '静态拉伸，感受牵拉感即可，避免疼痛' :
                             a.category === 'activation' ? '激活练习，控制速度、感受目标肌肉发力' :
                             '活动度练习，在无痛范围内缓慢进行'}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent">
                      {todayRecoveryCompleted ? (
                        <div className="flex items-center justify-center gap-2 py-3 bg-success/10 rounded-xl">
                          <Check className="w-4 h-4 text-success" />
                          <span className="text-sm font-bold text-success">今日恢复已完成</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => navigate('/recovery-workout')}
                          className="w-full btn-primary-gradient py-3 rounded-xl text-sm font-bold text-white"
                        >
                          开始跟练
                        </button>
                      )}
                    </div>
                  </SheetContent>
                </Sheet>
              )}
              {recoveryStatus !== 'good' && !todayInjuryRecovery && (
                <button
                  onClick={() => navigate('/recovery-workout')}
                  className="w-full text-left"
                >
                  <p className="text-xs text-muted-foreground">8 分钟 · 睡前完成</p>
                  <p className="text-[10px] text-primary mt-1.5">
                    {todayRecoveryCompleted ? '今日已完成 ✓' : '开始跟练 →'}
                  </p>
                </button>
              )}
            </div>
          )}
        </div>

        {/* 底部：非会员转化引导 */}
        {!member.isMember && (
          <button
            onClick={() => navigate('/subscribe')}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 border-t border-border/40 bg-gradient-to-r from-primary/5 to-secondary/5 text-xs font-medium text-foreground hover:from-primary/10 hover:to-secondary/10 transition-colors"
          >
            <Crown className="w-3.5 h-3.5 text-primary" />
            {todayTrainedOrDieted
              ? '今天已完成打卡，升级会员查看完整数据分析'
              : '升级会员，解锁今日恢复方案与专属训练计划'}
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>
       {/* /今日计划三合一 Tab 卡 */}

       {/* 健康录入 Sheet（所有用户可见） */}
      <HealthCheckInSheet
        open={healthCheckInOpen}
        onOpenChange={setHealthCheckInOpen}
      />

      {/* 健康打卡日历（首页胶囊入口） */}
      <HealthCalendarSheet
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
      />

       {/* /健康录入 Sheet */}

      {/* 有氧/恢复日详情弹窗 */}
       <AerobicDetailSheet
          open={aerobicOpen}
          onOpenChange={setAerobicOpen}
          day={todayMemberDay && todayMemberDay.type !== 'strength' ? todayMemberDay : null}
          reductionPercent={todayWorkout.volumeReductionPercent}
          abnormalCount={todayWorkout.abnormalIndicatorCount}
          shouldRest={todayWorkout.shouldRestToday}
          isToday={true}
          isCompleted={todayAerobicCompleted}
          onMarkComplete={markTodayAerobicComplete}
        />

      {/* 会员专属功能解锁提示弹窗 */}
      <MemberLockDialog
        open={memberLockOpen}
        onOpenChange={setMemberLockOpen}
        featureName="恢复建议"
        description="开通会员后即可解锁恢复建议，包括个性化伤病恢复、睡眠恢复跟练等专属内容"
      />

      {/* 头像昵称编辑弹窗 */}
      <ProfileEditDialog
        open={profileEditOpen}
        onOpenChange={setProfileEditOpen}
        currentName={displayName}
        currentAvatar={displayAvatar}
        onSave={handleSaveProfile}
      />

    </div>
  );
}

// 拍照日提醒：今日是节点日且未拍照时显示（仅会员）
function PhotoDayReminder() {
  const navigate = useNavigate();
  const { member } = useMemberStatus();
  const { isTodayPhotoDay, isTodayPhotoTaken, currentDay } = useComparePhotos();

  if (!member.isMember) return null;
  if (!isTodayPhotoDay || isTodayPhotoTaken) return null;

  return (
    <button
      onClick={() => navigate('/cycle-report')}
      className="w-full metallic-card rounded-2xl p-4 mb-3 flex items-center gap-3 text-left hover:shadow-md transition-shadow"
    >
      <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
        <Camera className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground">
          今日是拍照日 · 第 {currentDay} 天
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          记得拍一张对比照，记录你的变化
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </button>
  );
}
